import { Component, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { APP_CONFIG } from '../../environment';
import { LoggerProvider, LoggerLevel, LoggerEvent } from '../../providers/logger/logger';

declare let mapboxgl, mapboxSdk;

export enum MapLocatorType {
  LAT_LONG,
  ADDRESS
}
@Component({
  selector: 'rmt-map',
  templateUrl: 'rmt-map.html'
})
export class RmtMapComponent {
  static mapPromise;
  static mapGeoCoderPromise;
  static mapStyleSheetPromise;
  static mapScript;

  @Input() showLoader: boolean = true;
  @Input() address: string;
  @Input() lat: number;
  @Input() long: number;
  @Input() type: MapLocatorType;
  mapElementId;
  static mapElementIdCounter = 1;
  static retryTime = 2500;

  @Output() init = new EventEmitter<RmtMapComponent>();
  @ViewChild('map') mapElement;
  geocoder;
  map;
  loaded: boolean = false;
  markers = [];
  mapOptions = {
    zoom: 12
  };
  language;

  constructor() { 
    RmtMapComponent.retryTime = 2500; // reset retry time
    this.mapElementId = '_map_uid_' + RmtMapComponent.mapElementIdCounter;
    RmtMapComponent.mapElementIdCounter++;
    if (RmtMapComponent.mapElementIdCounter > 10000)
      RmtMapComponent.mapElementIdCounter = 1;
  }

  ngAfterViewInit() {
    RmtMapComponent.initMapbox(this, () => {
      this.initMap();
    });
  }

  static async initMapbox(source, initFunc) {
    if (!this.mapPromise) {
      // Inject the maps script into the page
      const url = `${APP_CONFIG.maps.baseUrl}`;
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.MapInit, 'Load map scripts', url);
      this.mapPromise = new Promise((resolve, reject) => {
        const old = document.getElementById('mapbox');
        if (old && document.parentNode) document.parentNode.removeChild(old);
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'mapbox';
        script.async = true;
        script.defer = true;
        script.src = url;
        script.onload = () => resolve(true);
        script.onerror = (err) => {
          LoggerProvider.exception(this, err, LoggerEvent.ComponentException, 'Error when loading mapbox from URL' + url);
          reject(err);
        }
        document.body.appendChild(script);
      });
    }
    if (!this.mapGeoCoderPromise) {
      const geoCoderUrl = `${APP_CONFIG.maps.geoCoderUrl}`;
      this.mapGeoCoderPromise = new Promise((resolve, reject) => {
        const old = document.getElementById('mapboxgeo');
        if (old && document.parentNode) document.parentNode.removeChild(old);
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'mapboxgeo';
        script.async = true;
        script.defer = true;
        script.src = geoCoderUrl;
        script.onload = () => resolve(true);
        script.onerror = (err) => {
          LoggerProvider.exception(this, err, LoggerEvent.ComponentException, 'Error when loading geocoder from URL' + geoCoderUrl);
          reject(err);
        }
        document.body.appendChild(script);
      });
    }
    if (!this.mapStyleSheetPromise) {
      const styleSheetUrl = `${APP_CONFIG.maps.styleSheet}`;
      this.mapStyleSheetPromise = new Promise((resolve, reject) => {
        const old = document.getElementById('mapboxcss');
        if (old && document.parentNode) document.parentNode.removeChild(old);
        const link = document.createElement('link');
        link.id = 'mapboxcss';
        link.href = styleSheetUrl;
        link.rel = 'stylesheet';
        link.onload = () => resolve(true);
        link.onerror = (err) => {
          LoggerProvider.exception(this, err, LoggerEvent.ComponentException, 'Error when loading mapbox stylesheet from URL' + styleSheetUrl);
          reject(err);
        }
        document.head.appendChild(link);
      });
    }

    let step = 'mapbox';
    try {
      await this.mapPromise;
      step = 'geocoder';
      await this.mapGeoCoderPromise;
      step = 'stylesheet';
      await this.mapStyleSheetPromise;
      step = 'mapboxgl';
      if (!mapboxgl)
        throw new Error('mapboxgl not initialied')
      step = 'init';
      initFunc();
    }
    catch (ex) {
      LoggerProvider.exception(source, ex, LoggerEvent.ComponentException, 'Failed to initialize mabox at step ' + step + ' - will retry in ' + RmtMapComponent.retryTime);
      this.mapPromise = null;
      this.mapGeoCoderPromise = null;
      this.mapStyleSheetPromise = null;
      window.setTimeout(() => {
        this.initMapbox(source, initFunc);
      }, RmtMapComponent.retryTime);
      RmtMapComponent.retryTime *= 2;
      if (RmtMapComponent.retryTime >= 30000)
        RmtMapComponent.retryTime = 30000;
    }
  }

  private initMap() {
    try {
      mapboxgl.accessToken = APP_CONFIG.maps.apiKey;
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.MapInit, 'Map initialized');
      var mapboxClient = mapboxSdk({
        accessToken: mapboxgl.accessToken,
      })
      this.geocoder = mapboxClient.geocoding;
      
        this.init.emit(this);
        if (this.lat && this.long) {
          this.setLatLngWithMarker(this.lat, this.long);
        }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to init map');
    }
  }

  setLatLngWithMarker(lat, lng) {
    try {
      this.setCenter(lat, lng);

      this.map.setZoom(16);

      new mapboxgl.Marker(this.getMarkerIcon())
        .setLngLat(new mapboxgl.LngLat(lng, lat))
        .addTo(this.map);
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to init set lat/lng with marker');
    }
  }

  showMap(mapOptions) {
    try {
      mapOptions = { ...this.mapOptions, ...mapOptions };
      this.map = new mapboxgl.Map({
        container: this.mapElementId, // container ID
        style: 'mapbox://styles/apiel-riafinancial/ck7oyreha010m1in8pq0rwz9u', // style URL
        ...mapOptions
      })
      this.loaded = true;
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to show map');
    }
  }

  setCenter(lat, long) {
    try {
      const latLng = new mapboxgl.LngLat(long, lat);
      if (!this.map) {
        const mapOptions = { ...this.mapOptions, center: latLng };
        this.showMap(mapOptions);
      } else this.map.setCenter(latLng);
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to set center');
    }
  }

  getCenter() {
    return this.map.getCenter();
  }

  setZoomAndPan(lat, lng) {
    try {
      if (this.map) {
        const latLong = new mapboxgl.LngLat(lng, lat);

        this.map.setZoom(12);
        this.map.panTo(latLong);
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to set zoom and pan');
    }
  }

  setPanAndZoomForLocation(lat, lng) {
    try {
      if (this.map) {
        const latLong = new mapboxgl.LngLat(lng, lat);

        this.map.setZoom(17);
        this.map.panTo(latLong);
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to set zoom and pan');
    }
  }

  search(address, center = null) {
    return new Promise(resolve => {
      if (!address) resolve(null);

      if (!center && this.map) center = this.map.getCenter();
      const config = {
        query: address,
        proximity: [center.lng,center.lat],
        language: [this.language]
      };
      this.geocoder.forwardGeocode(config).send()
      .then(response => {
        resolve(response.body.features);
      });

    });
  }

  showResult(result) {
    try {
      if (result.bbox) {
        const bounds = new mapboxgl.LngLatBounds([result.bbox[0], result.bbox[1]], [result.bbox[2], result.bbox[3]]);
        this.map.fitBounds(bounds, {animate: false});
      } else {
        this.map.flyTo({center: result.geometry.coordinates, animate: false});
      }
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to show result');
    }
  }

  clearMarkers() {
    for (let i = 0; i < this.markers.length; i++) {
      this.markers[i].remove();
    }
    this.markers = [];
  }

  createMarker(lat, long, index) {
    try {
      let match = 0;
      for (let i = 0; i < this.markers.length; i++) {
        if (Math.abs(this.markers[i].getLngLat().lat - lat) <= 0.00005 &&
          Math.abs(this.markers[i].getLngLat().lng - long) <= 0.00005) {
          if (match >= 8)
            match = 0;
          if (match === 0) // left middle
            long -= 0.00025;
          else if (match === 1) // left top
            lat += 0.00025;
          else if (match === 2 || match === 3) // middle top + right top
            long += 0.00025;
          else if (match === 4 || match === 5) // right middle + right bottom
            lat -= 0.00025;
          else if (match === 6 || match === 7) // middle bottom + left bottom
            long -= 0.00025;
          match++;
        }
      }

      const marker = new mapboxgl.Marker(this.getMarkerIcon(index)).setLngLat(new mapboxgl.LngLat(long, lat))
        .addTo(this.map);
      this.markers.push(marker);
      return marker;
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to create marker');
    }

    return null;
  }

  getMarkerIcon(index: string = null) {
    try {
      const icon = document.createElement('div');
      icon.className = 'marker location';
      if(parseInt(index) >= 0 && index != null){
        icon.className = 'marker';
      }
      icon.addEventListener('click', function(e){
        var x = document.getElementsByClassName("marker selected-marker");
        var i;
        for (i = 0; i < x.length; i++) {
          x[i].className = "marker";
        }
        this.className = 'marker selected-marker';
      })
      return icon;
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ComponentException, 'Failed to get marker icon');
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-map/rmt-map.ts